import React, { FC, Suspense } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { dashboardComponents } from '../../../constants/dashboardConstants';
import './dashboardItem.scss';
import LoadingSpinner from '../../loadingSpinner/loadingSpinner';
import EmptyDashboard from '../dashboardLayout/emptyDashboard';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import { DiggerPlanEnum } from '../../enums/DiggerPlanEnum';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardDesignProps {
  onLayoutChange?: (_newLayout: any) => Promise<void>,
  layout: any,
}

const calculateRowHeight = (desiredHeight: number, rows: number) => {
  const totalGapHeight = 15 * (rows - 4);
  const totalRowsHeight = desiredHeight * rows + totalGapHeight;
  return totalRowsHeight / rows;
};

const disabledDashboardComponents = {
  [DiggerPlanEnum.lite]: ['ProfitableAds', 'AdsThatLoseMoney', 'SeoReport', 'ReferrerReport'],
  [DiggerPlanEnum.standard]: ['SeoReport', 'ReferrerReport'],
};

const DashboardItem: FC<DashboardDesignProps> = (props) => {
  const numRows = Math.max(...props.layout.map((item: any) => item.y + item.h));
  const rowHeight = calculateRowHeight(105, numRows);
  const productId = useOrganizationSettingsStore((state) => state.organizationSettings.productId);

  return (
    <ResponsiveGridLayout
      data-cy="dashboard-layout"
      breakpoints={{
        lg: 996, md: 768, sm: 480, xs: 576, xxs: 1,
      }}
      cols={{
        lg: 16, md: 16, sm: 16, xs: 16, xxs: 16,
      }}
      className="relative min-h-[800px]"
      rowHeight={rowHeight}
      containerPadding={[0, 0]}
      margin={[15, 15]}
      draggableHandle="#drag-handle"
      onLayoutChange={props.onLayoutChange}
    >
      {!props.layout.length && (
        <div
          key="empty-dashboard"
          className="relative flex h-full w-full flex-col items-center"
          data-grid={{
            w: 16, h: 4, x: 0, y: 0,
          }}
        >
          <EmptyDashboard />
        </div>
      )}
      {props.layout.map((item: any) => {
        const DashboardElement = dashboardComponents[item.i];

        if ((productId === DiggerPlanEnum.lite || productId === DiggerPlanEnum.standard)
          && disabledDashboardComponents[productId].includes(item.i)) {
          return <div />;
        }

        return (
          <div
            key={item.i}
            data-grid={{
              w: item.w, h: item.h, x: item.x, y: item.y,
            }}
            className="z-10 rounded-[8px] border border-gray bg-white"
          >
            <div className="absolute z-10 h-12 w-[calc(100%-60px)]" id="drag-handle" />
            <Suspense fallback={<LoadingSpinner center color="primary" />}>
              <DashboardElement />
            </Suspense>
          </div>
        );
      })}
    </ResponsiveGridLayout>
  );
};

export default DashboardItem;
